(ns io.daskul.webapp.widgets.billing.billables-logic
  "Pure discount/total logic for the billables picker — React-free so it runs
   under the :node-test build and stays testable in isolation (separate logic
   from layout). The view namespace (billables-picker) re-exports these.

   Mirrors the backend resolver (billing/discount_resolution.clj).")

(defn compute-discount-amount
  "Compute the discount amount for a line item."
  [value-type value unit-price quantity]
  (case value-type
    "fixed-amount"  value
    "percentage"    (* unit-price quantity (/ value 100.0))
    0))

(defn discount-matches-item?
  "True when a discount applies to a billable item — mirrors the server
   resolver (discount_resolution.clj): the item is discountable, the offering
   matches, and the discount's beneficiary is either universal (nil) or the
   item's own beneficiary.

   Case asymmetry is deliberate and load-bearing: discounts are raw DB rows so
   their keys are snake_case (:offering_id, :beneficiary_id); items are built by
   enrollment->line-item so theirs are kebab-case (:offering-id, :beneficiary-id).
   The pre-rename keys (:package_id / :package-id) both went stale in sms-b26,
   which is why matching silently stopped (#838)."
  [d item]
  (and (:discountable item)
       (= (:offering_id d) (:offering-id item))
       (let [b (:beneficiary_id d)]
         (or (nil? b) (= b (:beneficiary-id item))))))

(defn compute-discount-assignments
  "Match discounts to selected items by offering + beneficiary.

   Returns {[pi ii] -> {:discount :discount-type :discount-note :discount-id
                         :description}}.

   When multiple discounts match the same item, the largest amount wins."
  [panels selected discounts excluded-discount-ids]
  (if (or (empty? discounts) (empty? selected))
    {}
    (let [active (remove #(contains? excluded-discount-ids (:id %)) discounts)
          sorted-items
          (->> selected
               (map (fn [[pi ii]]
                      (let [item (get-in panels [pi :items ii])]
                        {:coords [pi ii]
                         :item item
                         :value (* (double (:unit-price item 0))
                                   (double (:quantity item 1)))})))
               (sort-by :value >))]
      (reduce
       (fn [assignments {:keys [coords item]}]
         (let [candidates (seq (filter #(discount-matches-item? % item) active))]
           (if candidates
             (let [best (->> candidates
                             (map (fn [d]
                                    {:d d
                                     :amt (compute-discount-amount
                                           (:value_type d) (double (:value d))
                                           (double (:unit-price item 0))
                                           (double (:quantity item 1)))}))
                             (apply max-key :amt))
                   d (:d best)]
               (assoc assignments coords
                      {:discount (:amt best)
                       :discount-type (:discount_type d)
                       :discount-note (get-in d [:data :note])
                       :discount-id (:id d)
                       :description (:description item)}))
             assignments)))
       {}
       sorted-items))))

(defn compute-total
  "Sum (unit-price * quantity - assigned discount) for all selected items.
   Invoice-level discounts are subtracted from total when selected."
  ([panels selected assignments]
   (compute-total panels selected assignments nil nil))
  ([panels selected assignments invoice-discounts selected-invoice-discounts]
   (let [items-total
         (->> selected
              (map (fn [[pi ii]]
                     (let [item (get-in panels [pi :items ii])
                           base (* (:quantity item 1) (:unit-price item 0))
                           disc (or (:discount (get assignments [pi ii])) 0)]
                       (- base disc))))
              (reduce + 0))
         invoice-disc-total
         (if (and invoice-discounts selected-invoice-discounts)
           (->> selected-invoice-discounts
                (map (fn [idx] (get invoice-discounts idx)))
                (filter some?)
                (map (fn [d] (double (:value d 0))))
                (reduce + 0))
           0)]
     (- items-total invoice-disc-total))))
